import { getFormProps, getInputProps, getTextareaProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import  { type MetaFunction, type LoaderFunctionArgs, type ActionFunctionArgs } from '@remix-run/node'
import { Form, Link, json, redirect, useActionData, useLoaderData } from '@remix-run/react'
import { useEffect, useRef, useState } from 'react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { brandName, brandUrl } from "#app/components/branding.js"
import { ErrorList, Field, TextareaField } from '#app/components/forms.js'
import { TagsInputField } from '#app/components/ui/input-tags.js'
import { StatusButton } from '#app/components/ui/status-button.js'
import { getUserId, requireUserId } from '#app/utils/auth.server.js'
import { addBuildToServer } from '#app/utils/builds.server.js'
import { prisma } from '#app/utils/db.server.js'
import { checkHoneypot } from '#app/utils/honeypot.server.js'
import { useIsPending } from '#app/utils/misc.tsx'
import { NewBuildSchema, buildRatingMeter, sanitizeSlug } from '#app/utils/scripts.helper.js'
import { getServerDetailList } from '#app/utils/selected-server-options.js'
import { getServerBySlug, getServerDetailsBySlug } from '#app/utils/shards.server.js'
import { useOptionalUser } from '#app/utils/user.js'

export const meta: MetaFunction<typeof loader> = ({data}) => {
    const serverDetails = data?.serverDetails;
    return [
    { title: `Add UO Build to ${serverDetails?.name}` },
    { name: 'description', content: `Add a new build template for ${serverDetails?.name} on ${brandName}` },
]}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
    const serverDetailList = getServerDetailList();
    const serverDetails = serverDetailList.find(x => x.slug === params.serverSlug);
    if (!serverDetails) {
        return redirect('/builds/new');
    }

    const serverDetailsExpanded = await getServerDetailsBySlug(serverDetails.slug)
    const user = await getUserId(request);

    if (user && serverDetailsExpanded) {
        const shardIds = serverDetailsExpanded.shards.map(shard => shard.id);
        const charactersOnServer = await prisma.character.findMany({
            where: { userId: user, shardId: { in: shardIds } },
            include: {
              user: {
                select: {
                  id: true,
                  username: true,
                  image: true
                },
              },
            },
          });

          return json({
            serverDetails: serverDetails,
            charactersOnServer: charactersOnServer,
            result: {
                slug: "",
                name: "",
                description: "",
                gear: "",
                build: "",
                tags: [],
                character: "",
                difficulty: "",
            }
          });
       
    }


    return json({
        serverDetails: serverDetails,
        charactersOnServer: [],
        result: {
            slug: "",
            name: "",
            description: "",
            gear: "",
            build: "",
            tags: [],
            character: "",
            difficulty: "",
        }
      });
}

export async function action({ request, params }: ActionFunctionArgs) {
    const userId = await requireUserId(request);
    const serverDetails = await getServerBySlug({slug: params.serverSlug || ""});
    if (!serverDetails) {
        return redirect('/builds/new?whatserveristhis');
    }

    // const result = await getBuildBySlug(params.scriptSlug || "")
	// invariantResponse(result, 'Build not found', { status: 404 })

	const formData = await request.formData()
	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: NewBuildSchema.superRefine(async (data, ctx) => {
			const existingScript = await prisma.userBuildTemplate.findFirst({
				where: { slug: sanitizeSlug(data.name, userId), serverId: serverDetails.id },
				select: { id: true },
			})
			if (existingScript) {
				ctx.addIssue({
					path: ['slug'],
					code: z.ZodIssueCode.custom,
					message: 'A build with this slug already exists',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

    const tags = submission.value.tags.map((tag: string) => tag.toLowerCase().trim()).filter(Boolean)
    const tagsCleaned = tags[0]?.split(",")

    const response = await addBuildToServer(
        { 
            userId: userId,
            serverId: serverDetails.id,
            slug: sanitizeSlug(submission.value.name || "", userId),
            name: submission.value.name || "",
            description: submission.value.description || "",
            gear: submission.value.gear || "",
            build: submission.value.build || "",
            character: submission.value.character || "",
            difficulty: submission.value.difficulty || 0,
            tags: tagsCleaned ?? [],
        })

    if (response.status === 'success') {
        return redirect(response.redirect)
    } else {
        return json(
            {
                result: submission.reply({ formErrors: [response.error.message] }),
            },
            {
                status: 500,
            },
        )
    }
}

export default function AddNewBuild() {
  const user = useOptionalUser()
  const data = useLoaderData<typeof loader>()

  return <div className="container mb-48 mt-6 flex flex-col items-center justify-center">
		<div className="flex flex-wrap items-center justify-center">
			<h1 className="text-center text-h2">Add UO Build Template for {data.serverDetails.name}</h1>
		</div>
		<p className="mt-2 text-center text-muted-foreground">
			Skills are filtered to only show from the UO server you selected. If something is missing or incorrect please <Link to="/contact" className="underline hover:no-underline">contact us</Link>.
		</p>
		<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">
            <img src={data.serverDetails.logo} alt={data.serverDetails.name} className='max-h-28' />
            <div className='text-center'>
                <div className='flex flex-row gap-5 w-full text-center mt-4'>
                    &lt; 
                    <Link to="/builds/new" className='underline hover:no-underline'>
                        Change Server
                    </Link>
                    | 
                    <Link to={`/builds/${data.serverDetails.slug}`} className='underline hover:no-underline'>View {data.serverDetails.name} Builds</Link>
                    &gt;
                </div>
            </div>
			<div className="flex flex-col w-full">
                {user ? <></> : <div className='w-full mt-8'><div className='p-4 italic error-container w-1/2 text-center m-auto'>Be sure to <Link to={`/login?redirectTo=/builds/${data.serverDetails.slug}/new`} className='underline hover:no-underline'>login</Link> to save and share your build</div></div>}
                <SkillBuilder skillList={data.serverDetails.skills} />
			</div>
		</div>

	</div>
}


    const SkillSelection = ({skillList, currentBuild}:{skillList: string[] | undefined, currentBuild: BuildInfo[]}) => {
        if (!skillList || skillList.length <= 0) 
            return ""

      return (
          skillList.sort().map((x) => {
            if (!currentBuild.length || !currentBuild.find(f => f.name === x.trim())) {
                return <option key={x.trim()}>{x.trim()}</option>
            }
            return ""
        })
    )
  }

const filterValueReadable = (razorValue: string) => {
    if (typeof razorValue === "undefined") return 100;

    var returnValue = Number(razorValue);
    var hasDecimal = razorValue.split(".");
    if (hasDecimal.length === 2 && hasDecimal[1] === "0") {
        returnValue = Number(hasDecimal[0]) ?? 100;
    }
   
    if (returnValue <= 0) returnValue = 0;
    if (returnValue >= 120) returnValue = 120;
    return returnValue;
}



const fitlerNameQueryString = (cleanName: string) => {
  switch (cleanName) {
      case "Animal Lore":
          return "AnimalLore";
      case "Item Identification":
          return "ItemID";
      case "Carpentry/Bowcraft":
          return "Carpentry";
      case "Arms Lore":
          return "ArmsLore";
      case "Blacksmithy":
          return "Blacksmith";
      case "Detecting Hidden":
          return "DetectHidden";
      case "Evaluating Intelligence":
          return "EvalInt";
      case "Forensic Evaluation":
          return "Forensics";
      case "Inscription":
          return "Inscribe";
      case "Resisting Spells":
          return "MagicResist";
      case "Spirit Speaking":
          return "SpiritSpeak";
      case "Animal Taming":
          return "AnimalTaming";
      case "Taste Identification":
          return "TasteID";
      case "Swordsmanship":
          return "Swords";
      case "Mace Fighting":
          return "Macing";
      case "Remove Trap":
          return "RemoveTrap";
      default:
          return cleanName;
  }
}




// const getBuildFromTemplateString = (template: string) => {
//     if (typeof template === "undefined" || !template.length) {
//         return [];
//     }
//     var rows = template.split(",");
//     var pastedData = [];
//     rows.map(x => {
//         var row = x.split("|").filter(y => y != "");
//         var filteredName = filterNameReadable(row[0]);
//         if (skillList.indexOf(filteredName) >= 0 && !pastedData.find(x => x.name === filteredName)) {
//             let skillValue = (row.length === 2 && !isNaN(row[1])) ? row[1] : 100;
//             if (skillValue <= 0) skillValue = 0;
//             if (skillValue >= 120) skillValue = 120;
//             pastedData.push({
//                 name: filteredName,
//                 value: skillValue
//             })
//         };
//     });
//     return pastedData;    
// };

type BuildInfo = {
    name: string,
    value: number
}

const SkillBuilder = ({skillList}:{skillList: string[] | undefined}) => {
    const user = useOptionalUser()
    const data = useLoaderData<typeof loader>()
    const actionData = useActionData<typeof action>()
    const isPending = useIsPending()

    const [{error, errorMessage}, setError] = useState({error: false,errorMessage:""})
    const [build, setBuild] = useState<BuildInfo[]>([]);
    const [total, setTotal] = useState(0);
    const skillNameRef = useRef(null);
    const skillTotalRef = useRef(null);
    const pasteSkillsRef = useRef(null);

    useEffect(() => {
        let calcTotal = 0;
        //let useTemplateValues = "";
        build.map(b => {
            calcTotal += Math.round(b.value);
            //useTemplateValues += `${b.name}|${b.value},`;
        });
        setTotal(calcTotal);
    },[build]);
   
    

    const parseFromPaste = () => {
        if (!pasteSkillsRef || pasteSkillsRef.current === null) 
            return;
        
        var data = (pasteSkillsRef.current as HTMLInputElement).value;
        var rows = data.split(String.fromCharCode(10)) as string[];
        var pastedData = [] as any[];
        rows.map((x: string) => {
            let skillName = ""
            let parseValue = x.split(".");
            if (!parseValue.length) {
                return;
            }
            let skillNameParse = parseValue[0];

            if (typeof skillNameParse === "undefined" || skillNameParse.length <= 0) {
                return;
            }

            let skillNameCheck = skillNameParse.split(" ").filter(y => y != "");

            const skillValuePosition = skillNameCheck.length > 2 ? 3 : 2;

            if (skillNameCheck.length > 2) {
              skillName = skillNameCheck.slice(0,2).join(" ");
            } else {
              skillName = skillNameCheck.slice(0,1).join(" ");
            }
            var row = x.split(" ").filter(y => y != "");
            var rowValue = row[skillValuePosition];
            if (typeof rowValue !== "undefined" && Number(rowValue) > 0 && typeof skillList !== "undefined") {
                if (skillList.indexOf(skillName) >= 0 && !pastedData.find(x => x.name === skillName)) {
                    pastedData.push({
                        name: skillName,
                        value: filterValueReadable(rowValue)
                    })
                };
            }
        });
        setBuild(pastedData)
    }


	const [form, fields] = useForm({
		id: 'new-build-form',
		constraint: getZodConstraint(NewBuildSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: NewBuildSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
		defaultValue: {
			name: data.result.name,
			description: data.result.description,
			gear: data.result.gear,
			build: data.result.build,
            slug: data.result.slug,
			tags: data.result.tags,
			character: data.result.character,
            difficulty: data.result.difficulty,
		}
	})



    const handleSkillAdd = (event: any) => {
        event.preventDefault();

        if (skillNameRef === null || skillNameRef.current === null || skillTotalRef === null || skillTotalRef.current === null) {
            setError({ error: true, errorMessage: "You must select a template or skill"});
            return;
        }
        const skillNameRefDude = (skillNameRef.current as HTMLInputElement);

        if (skillNameRefDude.value === "Select a skill" || skillNameRefDude.value === "") {
            setError({ error: true, errorMessage: "You must select a template or skill"});
            skillNameRefDude.focus();
        } else {

            let skillValue = Number((skillTotalRef.current as HTMLInputElement).value);
            
            if (Number(skillValue) > 120 || Number(skillValue) < 0) {
                skillValue = 100;
            }

            setError({error: false, errorMessage: ""});
            setBuild([...build, { name: skillNameRefDude.value, value: skillValue }]);
            skillNameRefDude.focus();
        }
    }
   
    const changeSelectedSkill = (event: any, name: string) => {
        let x = [...build];
        let val = 0;
        if (typeof (event.target.value) !== "undefined" && !isNaN(event.target.value)) {
            val = event.target.value;
            if (val <= 0) val = 0;
            if (val >= 120) {
                event.target.value = 120
                val = 120
            };
        }
        if (val === 0) {
            x = x.filter((y)=> y.name !== name);
        } 
        else {
            const skillToUpdate = x.find((y) => y.name === name);
            if (skillToUpdate) {
                skillToUpdate.value = val;
            }
        }
        
        setBuild([...x]);
    }

    if (typeof skillList === "undefined" || skillList.length <= 0) {
        return <div className='m-8 p-4 italic error-container'>Uh oh! It looks like we can't load the skills for this server.</div>
    }

    const ShowExampleText = () => {
          return (
            <div className="example-text text-center">
                <p>Select a skill from the drop down and click apply</p>
                OR 
                <p>Import your character skills tab in Razor to get started</p>
            </div>
            )
        }

    return (
        <div className="">
            <Form method="POST" {...getFormProps(form)}>
            <HoneypotInputs />
            <div className="skill-bar-header">
                <div className="skill-bar p-4">
                    {
                        (error) 
                    ? 
                        <div>
                            <h5>{errorMessage}</h5>
                        </div>
                        : <></>
                    }
                    <select name="skill" ref={skillNameRef} className="text-dark dark:text-white p-2 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid">
                        <SkillSelection skillList={skillList} currentBuild={build} />
                    </select>
                    <input name="skill_value" type="number" defaultValue={100} size={3} ref={skillTotalRef} className='w-20 p-2 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid' />
                    <button onClick={handleSkillAdd} className='mx-2 text-button'>Apply</button>
                    {(total === 0) ? <button className='mx-2 text-button text-muted-foreground' disabled>Reset</button> : <button className='mx-2 text-button' onClick={(e) => {e.preventDefault();setBuild([]);}}>Reset</button> }
                </div>
                <div className="skill-and-bonus-container border-t mt-8 p-8 flex flex-row">
                    <div className="skill-scroll-container flex-grow">
                        <div className=''>
                            <div className="skill-container-top">
                                {(total === 0) ? "" : <input type="text" disabled defaultValue={fields.name.value} />}
                            </div>
                            <div className="skill-container skill-container-mid">
                                {build.sort( (a,b) => {
                                    if (a.name > b.name) {
                                        return 1;
                                    } else if (b.name > a.name) {
                                        return -1;
                                    }
                                    return 0;
                                }).map(x => { return <div className="skill-row" key={"selected_" + x.name}>
                                    <span className="name">{x.name}</span> <input type="number" min="0" max="120" onChange={(y) => { changeSelectedSkill(y,x.name) }} className="value" defaultValue={x.value} />
                                    </div>})
                                }
                                {(total === 0) ? <ShowExampleText /> : <div className="skill-row totals">
                                    <span className="name">Total</span> <span className={total > 720 ? "value invalid" : "value"}>{total}</span>
                                </div>}
                            </div>
                            <div className="skill-container-bot">&nbsp;</div>
                        </div>
                        <div className='mt-8'>
                            <Field
                                className="w-full"
                                labelProps={{
                                    htmlFor: fields.slug.id,
                                    children: 'Build Link Preview',
                                }}
                                inputProps={{
                                    ...getInputProps(fields.slug, { type: 'text' }),
                                    readOnly: true,
                                    value: `${brandUrl.toLocaleLowerCase()}/builds/${data.serverDetails.slug}/${sanitizeSlug(fields.name.value, user?.id)}`,
                                    autoFocus: true,
                                    disabled: true,
                                    autoComplete: 'off',
                                }}
                                errors={fields.slug.errors}
                            />
                        </div>
                    </div>
                    <div className="flex-grow p-4 min-w-96">
                        {
                            (total === 0)
                        ? 
                            <div className="">
                                <textarea ref={pasteSkillsRef} onChange={parseFromPaste} className='min-h-24 p-2 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid' placeholder="Click 'Copy All' from the Skills tab in Razor and Paste them here (CTRL-V)."></textarea>
                                <img className='my-4' src="/images/builds/copy-all-skills-razor.png" />
                            </div> 
                        : 
                            <div className='flex flex-col gap-4 p-4'>
                                <div>
                                    <Field
                                        className=""
                                        labelProps={{
                                            htmlFor: fields.name.id,
                                            children: 'Build Name (required)',
                                        }}
                                        inputProps={{
                                            ...getInputProps(fields.name, { type: 'text' }),
                                            autoFocus: true,
                                            placeholder: 'Name this build',
                                            autoComplete: 'off'
                                        }}
                                        errors={fields.name.errors}
                                    />

                                    <Field
                                        className="hidden"
                                        labelProps={{
                                            htmlFor: fields.build.id,
                                            children: 'Build Name (required)',
                                        }}
                                        inputProps={{
                                            ...getInputProps(fields.build, { type: 'text' }),
                                            readOnly: true,
                                            value: build.map(x => `${fitlerNameQueryString(x.name).toLowerCase()}|${x.value}`).join(","),
                                            autoFocus: false,
                                            autoComplete: 'off'
                                        }}
                                        errors={fields.build.errors}
                                    />
                                </div>
                                <div>         
                                    <TextareaField
                                        className="w-full"
                                        labelProps={{ children: 'Description (optional)' }}
                                        textareaProps={{
                                            ...getTextareaProps(fields.description),
                                            placeholder: 'Enter additional info about this build',
                                        }}
                                        errors={fields.description.errors}
                                    />               
                                </div>
                                <div className='w-full p-4'>
                                    <div className='flex justify-between text-sm mb-2'>
                                        <span>EASY</span>
                                        <span>HARDCORE</span>
                                    </div>
                                    <Field
                                        className=""
                                        labelProps={{
                                            htmlFor: fields.difficulty.id,
                                            children: '',
                                        }}
                                        inputProps={{
                                            ...getInputProps(fields.difficulty, { type: 'range' }),
                                            className: "w-full m-0 p-0",
                                            readOnly: true,
                                            min: 0,
                                            max: 10,
                                            defaultValue: fields.difficulty.value,
                                            autoFocus: false,
                                            autoComplete: 'off'
                                        }}
                                        errors={fields.difficulty.errors}
                                    />
                                    {/* <input type="range" min="0" max="10" defaultValue="5" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"/> */}
                                    <div className='text-center mt-2'>
                                        Newbie Friendliness: {buildRatingMeter(fields.difficulty.value)}
                                    </div>
                                </div>
                                <div>     
                                    <TagsInputField 
                                        className="w-full mb-0"
                                        labelProps={{
                                            htmlFor: fields.tags.id,
                                            children: 'Tags (add pvp or pvm at least)',
                                        }}
                                        inputProps={{
                                            ...getInputProps(fields.tags, { type: 'text' }),
                                            autoFocus: false,
                                            autoComplete: 'off',
                                        }}
                                        errors={fields.tags.errors}
                                    />
                                </div>
                                <div>                 
                                    <TextareaField
                                        className="w-full"
                                        labelProps={{ children: 'Gear Requirements (optional)' }}
                                        textareaProps={{
                                            ...getTextareaProps(fields.gear),
                                            placeholder: 'Enter info about required gear or setup',
                                        }}
                                        errors={fields.gear.errors}
                                    />           
                                </div>

                                
                                <div>                        
                                    Link to your character
                                    <select name="character" className="text-dark dark:text-white p-2 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid">
                                        <option value="">Optional</option>
                                        {data.charactersOnServer.length == 0 ? <option value="">No Characters Found</option> : data.charactersOnServer.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                                    </select>
                                </div>
                                <ErrorList errors={form.errors} id={form.errorId} />
        
                                <StatusButton
                                    className="w-full"
                                    status={isPending ? 'pending' : (form.status ?? 'idle')}
                                    type="submit"
                                    disabled={isPending || !user?.id.length}
                                >
                                    Submit your build {!user?.id.length ? " (Login Required)" : `as ${user?.username}`}
                                </StatusButton>
                            </div>
                        }
                    </div>          
                </div>
            </div> 
            <div className="share-link">
                Thanks for taking time to share your build!
            </div>
            </Form>
      </div>
    )
}

